"use client";

import * as React from "react";
import { AnimatePresence, m } from "motion/react";
import { cn } from "@marketing-sites/utils/cn";
import type { Logo } from "@marketing-sites/constants/logos";
import { Link } from "../../primitives";
import { LogoPill } from "./logo-pill";
import type { LogoWallLinks } from "./index";

type LogoSlot = { id: string; current: Logo; next: Logo; displayed: Logo };

const VISIBLE_COUNT = 12;
const INITIAL_DELAY_MS = 1000;
const ROTATION_MIN_MS = 2000;
const ROTATION_MAX_MS = 3000;

const defaultGetLogoAriaLabel = (logo: Logo) => `${logo.name} logo`;

/** Applied to whichever element is the direct child of the wrapping flex row. */
const CELL_WIDTH = "basis-[calc(33.333%-1rem)]";

const slotCountFor = (logos: Logo[]) =>
  Math.min(VISIBLE_COUNT, Math.ceil(logos.length / 2));

/** An odd logo count leaves the final logo with no pair, so it pairs with itself. */
const pairedWith = (logos: Logo[], index: number) =>
  logos[index + 1] ?? logos[index];

function buildInitialSlots(logos: Logo[]): LogoSlot[] {
  return Array.from({ length: slotCountFor(logos) }, (_, i) => {
    const current = logos[i * 2];
    return {
      id: `slot-${i}`,
      current,
      next: pairedWith(logos, i * 2),
      displayed: current,
    };
  });
}

export const LogoWallMobile = ({
  logos,
  links,
  className,
  ariaLabels,
}: {
  logos: Logo[];
  links?: LogoWallLinks;
  className?: string;
  ariaLabels?: Record<string, string>;
}) => {
  const slotCount = slotCountFor(logos);
  const hasUnpairedFinalSlot = slotCount * 2 > logos.length;
  const rotatingSlotCount = hasUnpairedFinalSlot ? slotCount - 1 : slotCount;
  const [slots, setSlots] = React.useState(() => buildInitialSlots(logos));
  const timerRef = React.useRef<NodeJS.Timeout | null>(null);
  const remainingSlotsRef = React.useRef<number[]>([]);

  const scheduleNextRotation = React.useCallback(() => {
    if (remainingSlotsRef.current.length === 0) {
      remainingSlotsRef.current = Array.from(
        { length: rotatingSlotCount },
        (_, i) => i,
      );
    }

    const randomIndex = Math.floor(
      Math.random() * remainingSlotsRef.current.length,
    );
    const slotIndex = remainingSlotsRef.current[randomIndex];
    remainingSlotsRef.current.splice(randomIndex, 1);

    setSlots((prev) => {
      const slot = prev[slotIndex];
      if (!slot) return prev;
      const next = [...prev];
      next[slotIndex] = { ...slot, current: slot.next, next: slot.current };
      return next;
    });

    const delay =
      ROTATION_MIN_MS + Math.random() * (ROTATION_MAX_MS - ROTATION_MIN_MS);
    timerRef.current = setTimeout(scheduleNextRotation, delay);
  }, [rotatingSlotCount]);

  // Delays the link href swap until the outgoing logo's exit animation finishes.
  const commitDisplayed = React.useCallback((slotId: string) => {
    setSlots((prev) => {
      const index = prev.findIndex((slot) => slot.id === slotId);
      const slot = prev[index];
      if (!slot || slot.displayed.name === slot.current.name) return prev;
      const next = [...prev];
      next[index] = { ...slot, displayed: slot.current };
      return next;
    });
  }, []);

  /**
   * Create a timer to schedule the next rotation
   */
  React.useEffect(() => {
    if (rotatingSlotCount === 0) return;

    timerRef.current = setTimeout(scheduleNextRotation, INITIAL_DELAY_MS);

    const onVisibilityChange = () => {
      if (document.hidden) {
        if (timerRef.current) clearTimeout(timerRef.current);
      } else {
        timerRef.current = setTimeout(scheduleNextRotation, INITIAL_DELAY_MS);
      }
    };
    document.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [rotatingSlotCount, scheduleNextRotation]);

  return (
    <div
      className={cn(
        "@3xl:hidden mx-auto my-6 w-full px-6",
        "@lg:gap-y-14 flex flex-wrap justify-center gap-x-6 gap-y-12",
        className,
      )}
    >
      {slots.map((slot) => {
        const link = links?.[slot.displayed.name];

        const animation = (
          <div
            key={slot.id}
            className={cn(
              "relative flex h-8 min-w-0 items-center justify-center",
              link ? "w-full" : CELL_WIDTH,
              !link && links && "opacity-40",
            )}
          >
            <AnimatePresence
              mode="wait"
              initial={false}
              onExitComplete={() => commitDisplayed(slot.id)}
            >
              <m.div
                key={slot.current.name}
                initial={{ opacity: 0, filter: "blur(10px)" }}
                animate={{ opacity: 1, filter: "blur(0px)" }}
                exit={{ opacity: 0, filter: "blur(10px)" }}
                transition={{ duration: 0.6 }}
                // Forces JS animation: motion@12.6.3's WAAPI path drops a frame of stale style on finish, flashing the old logo.
                onUpdate={() => {}}
                // Shrink-to-fit rather than clip: any clip would also cut off the blur.
                className={cn(
                  "absolute inset-0 flex items-center justify-center [&_svg]:max-w-full",
                )}
                style={{ willChange: "transform, opacity, filter" }}
                role="img"
                aria-label={
                  ariaLabels?.[slot.current.name] ??
                  defaultGetLogoAriaLabel(slot.current)
                }
              >
                {slot.current.logo}
              </m.div>
            </AnimatePresence>
          </div>
        );

        if (!link) {
          return animation;
        }

        return (
          <Link
            key={slot.id}
            // eslint-disable-next-line analytics/id-must-be-static -- forwarded from CustomerLogoWall, which composes it from a brand name in the checked-in logo registry
            analyticsId={link.analyticsId}
            href={link.href}
            aria-label={`${slot.displayed.name} — ${link.label}`}
            className={cn(
              "group/logo relative flex min-w-0 items-center justify-center opacity-40",
              CELL_WIDTH,
            )}
          >
            <LogoPill label={link.label} stat={link.stat} />
            {animation}
          </Link>
        );
      })}
    </div>
  );
};
